import { useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from '@tanstack/react-router'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table'
import { monitorApi, type SolutionGroup, type Project, SOURCE_PLATFORMS } from '@/lib/api/monitor'
import { Plus, Edit2, Trash2, FolderOpen, FileText, BarChart3, ChevronRight, Loader2, Globe } from 'lucide-react'

export function MonitorManagePage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [groups, setGroups] = useState<SolutionGroup[]>([])
  const [projects, setProjects] = useState<Record<string, Project[]>>({})
  const [expandedGroup, setExpandedGroup] = useState<string | null>(null)
  const [showGroupDialog, setShowGroupDialog] = useState(false)
  const [showProjectDialog, setShowProjectDialog] = useState(false)
  const [editingGroup, setEditingGroup] = useState<SolutionGroup | null>(null)
  const [editingProject, setEditingProject] = useState<Project | null>(null)
  const [groupName, setGroupName] = useState('')
  const [loading, setLoading] = useState(false)
  const [projectForm, setProjectForm] = useState<Partial<Project>>({
    project_type: 1,
    subject_word: '',
    character_word: '',
    event_word: '',
    regional_word: '',
    stop_word: '',
    source_platforms: ['weibo', 'wechat', 'news', 'app'],
    monitor_duration: 30,
  })

  // 时间范围预设选项（天）
  const durationPresets = [
    { value: 7, label: t('monitor.days7') },
    { value: 30, label: t('monitor.days30') },
    { value: 90, label: '90天' },
    { value: 180, label: '180天' },
    { value: 365, label: t('monitor.days365') || '1年' },
    { value: -1, label: t('monitor.custom') },
  ]

  const loadGroups = useCallback(async () => {
    setLoading(true)
    try {
      const res: any = await monitorApi.getGroupList(1)
      const list = res?.data || res || []
      setGroups(Array.isArray(list) ? list : [])
    } catch (err) {
      console.error('Failed to load groups:', err)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadGroups()
  }, [loadGroups])

  const loadProjects = async (groupId: string) => {
    try {
      const res: any = await monitorApi.getProjectList(groupId, 1)
      const list = res?.data || res || []
      setProjects(prev => ({ ...prev, [groupId]: Array.isArray(list) ? list : [] }))
    } catch (err) {
      console.error('Failed to load projects:', err)
    }
  }

  const handleToggleGroup = (groupId: string) => {
    if (expandedGroup === groupId) {
      setExpandedGroup(null)
    } else {
      setExpandedGroup(groupId)
      if (!projects[groupId]) {
        loadProjects(groupId)
      }
    }
  }

  const handleSaveGroup = async () => {
    if (!groupName.trim()) return
    try {
      if (editingGroup) {
        await monitorApi.updateGroup(editingGroup.group_id, groupName)
      } else {
        await monitorApi.addGroup(groupName)
      }
      setShowGroupDialog(false)
      setGroupName('')
      setEditingGroup(null)
      loadGroups()
    } catch (err) {
      console.error('Failed to save group:', err)
    }
  }

  const handleSaveProject = async () => {
    if (!projectForm.project_name?.trim()) return
    try {
      if (editingProject) {
        await monitorApi.updateProject(projectForm)
      } else {
        await monitorApi.addProject({ ...projectForm, group_id: expandedGroup || '' })
      }
      setShowProjectDialog(false)
      setEditingProject(null)
      setProjectForm({
        project_type: 1,
        subject_word: '',
        character_word: '',
        event_word: '',
        regional_word: '',
        stop_word: '',
        monitor_duration: 30,
      })
      if (expandedGroup) loadProjects(expandedGroup)
    } catch (err) {
      console.error('Failed to save project:', err)
    }
  }

  const handleDeleteGroup = async (groupId: string) => {
    if (!confirm(t('monitor.groupDeleteConfirm'))) return
    try {
      await monitorApi.deleteGroup(groupId)
      loadGroups()
    } catch (err) {
      console.error('Failed to delete group:', err)
    }
  }

  const handleDeleteProject = async (projectId: string) => {
    if (!confirm(t('monitor.deleteConfirm'))) return
    try {
      await monitorApi.deleteProject(projectId)
      if (expandedGroup) loadProjects(expandedGroup)
    } catch (err) {
      console.error('Failed to delete project:', err)
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold">{t('monitor.title')}</h2>
        <Button onClick={() => { setEditingGroup(null); setGroupName(''); setShowGroupDialog(true) }}>
          <Plus className="mr-2 h-4 w-4" />
          {t('monitor.addGroup')}
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('monitor.groupName')}</TableHead>
                <TableHead className="w-[200px]">{t('common.actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={2} className="text-center py-8">
                    <Loader2 className="mx-auto h-6 w-6 animate-spin text-muted-foreground" />
                  </TableCell>
                </TableRow>
              ) : groups.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={2} className="text-center text-muted-foreground py-8">
                    {t('common.noData')}
                  </TableCell>
                </TableRow>
              ) : (
                groups.map((group) => (
                  <GroupRow
                    key={group.group_id}
                    group={group}
                    projects={projects[group.group_id] || []}
                    expanded={expandedGroup === group.group_id}
                    onToggle={() => handleToggleGroup(group.group_id)}
                    onEditGroup={() => {
                      setEditingGroup(group)
                      setGroupName(group.group_name)
                      setShowGroupDialog(true)
                    }}
                    onDeleteGroup={() => handleDeleteGroup(group.group_id)}
                    onAddProject={() => {
                      setEditingProject(null)
                      setProjectForm({
                        project_type: 1,
                        subject_word: '',
                        character_word: '',
                        event_word: '',
                        regional_word: '',
                        stop_word: '',
                        source_platforms: ['weibo', 'wechat', 'news', 'app'],
                        monitor_duration: 30,
                      })
                      setShowProjectDialog(true)
                    }}
                    onEditProject={(project) => {
                      setEditingProject(project)
                      setProjectForm(project)
                      setShowProjectDialog(true)
                    }}
                    onDeleteProject={(project) => handleDeleteProject(project.project_id)}
                    onAnalyzeProject={(project) => {
                      navigate({ to: '/analysis', search: { projectId: project.project_id } })
                    }}
                    onViewData={(project) => {
                      navigate({ to: '/monitor/data', search: { projectId: project.project_id } })
                    }}
                  />
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Group Dialog */}
      <Dialog open={showGroupDialog} onOpenChange={setShowGroupDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingGroup ? t('monitor.editGroup') : t('monitor.addGroup')}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>{t('monitor.groupName')}</Label>
              <Input
                value={groupName}
                onChange={(e) => setGroupName(e.target.value)}
                placeholder={t('common.inputPlaceholder')}
                autoFocus
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowGroupDialog(false)}>
              {t('common.cancel')}
            </Button>
            <Button onClick={handleSaveGroup} disabled={!groupName.trim()}>
              {t('common.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Project Dialog */}
      <Dialog open={showProjectDialog} onOpenChange={setShowProjectDialog}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingProject ? t('monitor.editProject') : t('monitor.addProject')}
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2 col-span-2">
              <Label>{t('monitor.projectName')} *</Label>
              <Input
                value={projectForm.project_name || ''}
                onChange={(e) => setProjectForm({ ...projectForm, project_name: e.target.value })}
                placeholder={t('common.inputPlaceholder')}
                autoFocus
              />
            </div>
            <div className="space-y-2">
              <Label>{t('monitor.projectType')}</Label>
              <select
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={projectForm.project_type || 1}
                onChange={(e) => setProjectForm({ ...projectForm, project_type: Number(e.target.value) })}
              >
                <option value={1}>{t('monitor.normal')}</option>
                <option value={2}>{t('monitor.advanced')}</option>
              </select>
            </div>
            <div className="space-y-2 col-span-2">
              <Label>{t('monitor.subjectWord')}</Label>
              <Input
                value={projectForm.subject_word || ''}
                onChange={(e) => setProjectForm({ ...projectForm, subject_word: e.target.value })}
                placeholder={t('common.inputPlaceholder')}
              />
              <p className="text-xs text-muted-foreground">{t('monitor.subjectWordHint') || ''}</p>
            </div>
            <div className="space-y-2">
              <Label>{t('monitor.characterWord')}</Label>
              <Input
                value={projectForm.character_word || ''}
                onChange={(e) => setProjectForm({ ...projectForm, character_word: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>{t('monitor.eventWord')}</Label>
              <Input
                value={projectForm.event_word || ''}
                onChange={(e) => setProjectForm({ ...projectForm, event_word: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>{t('monitor.regionalWord')}</Label>
              <Input
                value={projectForm.regional_word || ''}
                onChange={(e) => setProjectForm({ ...projectForm, regional_word: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>{t('monitor.stopWord')}</Label>
              <Input
                value={projectForm.stop_word || ''}
                onChange={(e) => setProjectForm({ ...projectForm, stop_word: e.target.value })}
              />
            </div>
            <div className="space-y-2 col-span-2">
              <Label>{t('monitor.monitorDuration') || '数据时间范围'}</Label>
              <div className="flex items-center gap-3">
                <select
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={projectForm.monitor_duration === -1 ? -1 : (durationPresets.find(p => p.value === projectForm.monitor_duration)?.value ?? -1)}
                  onChange={(e) => {
                    const val = Number(e.target.value)
                    if (val === -1) {
                      setProjectForm({ ...projectForm, monitor_duration: projectForm.monitor_duration && projectForm.monitor_duration > 0 ? projectForm.monitor_duration : 30 })
                      // Switch to custom mode: keep current value or default 30
                      setProjectForm({ ...projectForm, monitor_duration: -1 })
                    } else {
                      setProjectForm({ ...projectForm, monitor_duration: val })
                    }
                  }}
                >
                  {durationPresets.map(p => (
                    <option key={p.value} value={p.value}>{p.label}</option>
                  ))}
                </select>
                {projectForm.monitor_duration === -1 && (
                  <div className="flex items-center gap-2 shrink-0">
                    <Input
                      type="number"
                      min={1}
                      max={365}
                      className="w-24"
                      value={projectForm.monitor_duration === -1 ? '' : projectForm.monitor_duration}
                      onChange={(e) => {
                        let val = Number(e.target.value) || 0
                        if (val > 365) val = 365
                        if (val < 1) val = 1
                        setProjectForm({ ...projectForm, monitor_duration: val })
                      }}
                      placeholder="1-365"
                    />
                    <span className="text-sm text-muted-foreground whitespace-nowrap">{t('monitor.days') || '天'}</span>
                  </div>
                )}
              </div>
              <p className="text-xs text-muted-foreground">{t('monitor.monitorDurationHint') || '设置方案的数据监测时间范围，最长1年（365天）'}</p>
            </div>
            <div className="space-y-2 col-span-2">
              <Label className="flex items-center gap-2">
                <Globe className="h-4 w-4" />
                {t('monitor.sourcePlatforms')}
              </Label>
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                {SOURCE_PLATFORMS.map((platform) => {
                  const selected = (projectForm.source_platforms || []).includes(platform.id)
                  return (
                    <button
                      key={platform.id}
                      type="button"
                      onClick={() => {
                        const current = projectForm.source_platforms || []
                        setProjectForm({
                          ...projectForm,
                          source_platforms: selected
                            ? current.filter((id) => id !== platform.id)
                            : [...current, platform.id],
                        })
                      }}
                      className={`flex items-center justify-center rounded-md border px-3 py-2 text-sm transition-colors ${
                        selected
                          ? 'border-primary bg-primary text-primary-foreground'
                          : 'border-input bg-background hover:bg-accent'
                      }`}
                    >
                      {platform.name}
                    </button>
                  )
                })}
              </div>
              <p className="text-xs text-muted-foreground">{t('monitor.sourcePlatformsHint') || ''}</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowProjectDialog(false)}>
              {t('common.cancel')}
            </Button>
            <Button onClick={handleSaveProject} disabled={!projectForm.project_name?.trim()}>
              {t('common.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

// Separate component for group row with expandable projects
function GroupRow({
  group,
  projects,
  expanded,
  onToggle,
  onEditGroup,
  onDeleteGroup,
  onAddProject,
  onEditProject,
  onDeleteProject,
  onAnalyzeProject,
  onViewData,
}: {
  group: SolutionGroup
  projects: Project[]
  expanded: boolean
  onToggle: () => void
  onEditGroup: () => void
  onDeleteGroup: () => void
  onAddProject: () => void
  onEditProject: (p: Project) => void
  onDeleteProject: (p: Project) => void
  onAnalyzeProject: (p: Project) => void
  onViewData: (p: Project) => void
}) {
  const { t } = useTranslation()

  return (
    <>
      <TableRow>
        <TableCell>
          <button
            className="flex items-center gap-2 font-medium hover:text-primary transition-colors"
            onClick={onToggle}
          >
            <ChevronRight className={`h-4 w-4 transition-transform ${expanded ? 'rotate-90' : ''}`} />
            <FolderOpen className="h-4 w-4" />
            {group.group_name}
            {projects.length > 0 && (
              <Badge variant="secondary" className="ml-1">
                {projects.length}
              </Badge>
            )}
          </button>
        </TableCell>
        <TableCell>
          <div className="flex gap-1">
            <Button variant="ghost" size="icon" onClick={onEditGroup} title={t('common.edit')}>
              <Edit2 className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="icon" onClick={onDeleteGroup} title={t('common.delete')}>
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </TableCell>
      </TableRow>
      {expanded && (
        <TableRow>
          <TableCell colSpan={2} className="bg-muted/20 py-3">
            <div className="ml-8 space-y-2">
              <Button size="sm" variant="outline" onClick={onAddProject}>
                <Plus className="mr-1 h-3 w-3" />
                {t('monitor.addProject')}
              </Button>
              {projects.map((project) => (
                <div key={project.project_id} className="flex items-center justify-between rounded-lg border bg-card p-3 hover:shadow-sm transition-shadow">
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
                    <span className="text-sm font-medium truncate">{project.project_name}</span>
                    <Badge variant={project.project_type === 2 ? 'default' : 'secondary'}>
                      {project.project_type === 2 ? t('monitor.advanced') : t('monitor.normal')}
                    </Badge>
                    {(project.source_platforms || []).length > 0 && (
                      <div className="hidden md:flex items-center gap-1 ml-2 flex-wrap">
                        {project.source_platforms.slice(0, 4).map((pid) => {
                          const platform = SOURCE_PLATFORMS.find(p => p.id === pid)
                          const label = platform ? platform.name : pid
                          return (
                            <Badge key={pid} variant="outline" className="text-xs font-normal">
                              {label}
                            </Badge>
                          )
                        })}
                        {(project.source_platforms || []).length > 4 && (
                          <span className="text-xs text-muted-foreground">
                            +{(project.source_platforms || []).length - 4}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <Button variant="ghost" size="sm" onClick={() => onAnalyzeProject(project)}>
                      <BarChart3 className="mr-1 h-3 w-3" />
                      {t('nav.analysis')}
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => onViewData(project)}>
                      <FileText className="mr-1 h-3 w-3" />
                      {t('nav.monitorData')}
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => onEditProject(project)}>
                      <Edit2 className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => onDeleteProject(project)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </TableCell>
        </TableRow>
      )}
    </>
  )
}
